import './entry_shared';

import Hotjar from '@hotjar/browser';
import { captureException, ErrorBoundary } from '@sentry/react';
import { AnalyticsService } from 'base/analytics';
import { getApiHost } from 'base/api_host';
import { injector } from 'base/dependency-container';
import { LibraryCacheImpl } from 'base/library_cache';
import { HttpLibraryService } from 'base/library_service';
import { LayerProvider } from 'base/ui/Layer';
import { OverlayHost } from 'base/ui/overlay';
import { UiPlayround } from 'base/ui/playground';
import { ToastQueue } from 'base/ui/toast';
import { installOverlay } from 'blocks/install_overlay';
import { usePageViewEvent } from 'blocks/usePageViewEvent';
import { observer } from 'mobx-react-lite';
import posthog from 'posthog-js';
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  getUserId,
  getUserJwt,
  logOutWithoutReload,
} from '../../library/src/modules/cookies';
import { AcceptTeamInvitePage } from './accept_team_invite/page';
import { maybeParseAcceptTeamInviteRoute } from './accept_team_invite/routes';
import { AnnouncementPlayground } from './announcement_playground';
import { AuthFlowPages } from './auth/page';
import { maybeParseAuthRoute } from './auth/routes';
import { HttpBlocksService } from './blocks_service';
import { CallbackPages } from './callbacks/page';
import { maybeParseCallbackRoute } from './callbacks/routes';
import { ClipboardInspector } from './clipboard_inspector';
import { installComponentIdMapperTool } from './component_renderer/id_mapper_tool';
import { installComponentRenderingPlayground } from './component_renderer/playground';
import { CreateProject } from './create_project';
import { currentUrl } from './current_url';
import { DashboardPage } from './dashboard/page';
import { maybeParseDashboardRoute } from './dashboard/routes';
import { installTextFittingPlayground } from './editor/ai/text_fitting_playground';
import { installEditor } from './editor/editor';
import {
  FeatureFlagsStore,
  FlagOverridesPage,
  readFeatureFlagCache,
  writeFeatureFlagCache,
} from './flag_overrides';
import './global.css';
import { LegacyEditorRedirect } from './legacy_editor_redirect';
import { HttpLLMProxyService } from './llm_proxy_service';
import {
  DebugHomePage,
  LoginWall,
  NotFoundPage,
  RedirectOnLoad,
  renderErrorFallback,
} from './login_wall';
import { ManageAccountPages } from './manage_account/page';
import { maybeParseManageAccountRoute } from './manage_account/routes';
import { NetworkCheckerPage } from './network_checker';
import { PageContainer } from './page_container';
import { Playground } from './playground';
import { createAccountService } from './services/account';
import { createHttpService } from './services/http';
import { createPreferenceService } from './services/preference';
import { Viewer } from './viewer';

const http = injector.resolve(createHttpService);

const { service: accountsService, store: accountStore } =
  injector.resolve(createAccountService);

const featureFlagsStore = new FeatureFlagsStore(readFeatureFlagCache());
accountsService.getFeatureFlags().then((flags) => {
  featureFlagsStore.sync(flags);
  writeFeatureFlagCache(flags);
});

const preferencesStore = injector.resolve(createPreferenceService);
const llmProxyService = new HttpLLMProxyService(
  http.getTokenLegacy,
  featureFlagsStore,
);
const productionLibraryService = new LibraryCacheImpl(
  new HttpLibraryService(http),
);
const stagingLibraryService = new LibraryCacheImpl(
  new HttpLibraryService(http, { useStagingComponents: true }),
);
const blocksService = new HttpBlocksService(http);
const analyticsService = new AnalyticsService({
  getToken: http.getTokenLegacy,
  getUserId,
  getContext: () => {
    if (accountStore.member == null) {
      return {};
    }

    return { userPlanName: accountStore.member.planName };
  },
  captureException,
});

const toastQueue = new ToastQueue();
const Editor = installEditor(
  accountStore,
  toastQueue,
  blocksService,
  accountsService,
  analyticsService,
  llmProxyService,
  { productionLibraryService, stagingLibraryService },
);
const ComponentRenderingPlayground = installComponentRenderingPlayground(
  productionLibraryService,
);
const TextFittingPlayground = installTextFittingPlayground(llmProxyService);
const ComponentIdMapper = installComponentIdMapperTool(
  productionLibraryService,
);

installOverlay({
  accountsService,
  analyticsService,
  accountStore,
  toastQueue,
  featureFlagsStore,
});
const EDITOR_PATH_RE = '^/app/project/(P[0-9]+_[^/]+)$';
const LEGACY_EDITOR_PATH_RE = '^/app/project/([0-9]+)$';
const LEGACY_VIWER_PATH_RE = '^/app/project/([0-9]+)/([^/]+)/view$';

const Router = observer(function _Router() {
  usePageViewEvent(analyticsService);

  const path = currentUrl.pathname;
  const search = currentUrl.searchParams;

  const authRoute = maybeParseAuthRoute(currentUrl);
  if (authRoute) {
    return (
      <AuthFlowPages
        accountStore={accountStore}
        route={authRoute}
        accountsService={accountsService}
        analytics={analyticsService}
      />
    );
  }

  const callbackRoute = maybeParseCallbackRoute(currentUrl);
  if (callbackRoute) {
    return (
      <CallbackPages
        route={callbackRoute}
        accountsService={accountsService}
        accountStore={accountStore}
        analytics={analyticsService}
      />
    );
  }

  const manageAccountRoute = maybeParseManageAccountRoute(currentUrl);
  if (manageAccountRoute) {
    return (
      <LoginWall>
        <OverlayHost>
          <ManageAccountPages
            accountStore={accountStore}
            featureFlagsStore={featureFlagsStore}
            route={manageAccountRoute}
            accountsService={accountsService}
            analytics={analyticsService}
          />
        </OverlayHost>
      </LoginWall>
    );
  }

  const acceptTeamInviteRoute = maybeParseAcceptTeamInviteRoute(currentUrl);
  if (acceptTeamInviteRoute) {
    return (
      <LoginWall path="signup" context={{ skipPricingTable: true }}>
        <AcceptTeamInvitePage
          route={acceptTeamInviteRoute}
          accountsService={accountsService}
        />
      </LoginWall>
    );
  }

  const isLoggedIn = !!getUserJwt();
  const dashboardRoute = maybeParseDashboardRoute(currentUrl);
  if (dashboardRoute) {
    if (!isLoggedIn) {
      return <RedirectOnLoad name="home" url="/" />;
    }
    return (
      <OverlayHost>
        <DashboardPage
          toastQueue={toastQueue}
          accountStore={accountStore}
          featureFlagsStore={featureFlagsStore}
          preferencesStore={preferencesStore}
          libraryService={productionLibraryService}
          blocksService={blocksService}
          accountsService={accountsService}
          analytics={analyticsService}
          isLoggedIn={isLoggedIn}
          route={dashboardRoute}
        />
      </OverlayHost>
    );
  }
  if (path === '/app/project/create') {
    return (
      <CreateProject
        accountStore={accountStore}
        analytics={analyticsService}
        blocksService={blocksService}
        brief={search.get('brief') || undefined}
      />
    );
  }

  const editorMatch = path.match(EDITOR_PATH_RE);
  if (editorMatch) {
    return (
      <OverlayHost>
        <Editor projectToken={editorMatch[1]} isLoggedIn={isLoggedIn} />
      </OverlayHost>
    );
  }
  const legacyEditorMatch = path.match(LEGACY_EDITOR_PATH_RE);
  if (legacyEditorMatch) {
    const [_all, id] = legacyEditorMatch;
    return (
      <LegacyEditorRedirect blocksService={blocksService} projectId={id} />
    );
  }
  if (path.startsWith('/app/preview/')) {
    return (
      <Viewer
        path={path}
        blocksService={blocksService}
        analyticsService={analyticsService}
        library={productionLibraryService}
      />
    );
  }
  const viewerMatch = path.match(LEGACY_VIWER_PATH_RE);
  if (viewerMatch) {
    const [_all, id, tokenSecret] = viewerMatch;
    return (
      <RedirectOnLoad
        name="viewer"
        url={`/app/project/P${id}_${tokenSecret}`}
      />
    );
  }

  if (path === '/app/logout') {
    logOutWithoutReload();
    return <RedirectOnLoad name="home" url={'/'} />;
  }
  if (path === '/app/flag-overrides') {
    return <FlagOverridesPage />;
  }
  if (path === '/app/component-rendering-playground') {
    return (
      <LoginWall>
        <ComponentRenderingPlayground />
      </LoginWall>
    );
  }
  if (path === '/app/text-fitting-playground') {
    return (
      <LoginWall>
        <TextFittingPlayground />
      </LoginWall>
    );
  }
  if (path === '/app/announcement-playground') {
    return <AnnouncementPlayground service={accountsService} />;
  }
  if (path === '/app/component-id-mapper') {
    return (
      <LoginWall>
        <ComponentIdMapper />
      </LoginWall>
    );
  }
  if (path === '/app/clipboard-inspector') {
    return <ClipboardInspector />;
  }
  if (path === '/app/ui-playground') {
    return <UiPlayround />;
  }
  if (path === '/app/playground') {
    return <Playground />;
  }

  if (path === '/app/network-checker') {
    return <NetworkCheckerPage />;
  }

  if (path === '/') {
    return <DebugHomePage />;
  }

  // For legacy links
  if (!path.startsWith('/app/')) {
    const { search, hash } = window.location;
    const withAppPrefix = `/app/${
      path.startsWith('/') ? path.slice(1) : path
    }${search}${hash}`;
    return <RedirectOnLoad name="app" url={withAppPrefix} replace />;
  }

  return <NotFoundPage />;
});

const reactContainer = document.querySelector('#react-container')!;
createRoot(reactContainer).render(
  <PageContainer>
    <ErrorBoundary fallback={renderErrorFallback}>
      <LayerProvider>
        <Router />
      </LayerProvider>
    </ErrorBoundary>
  </PageContainer>,
);

const { hostname } = window.location;
if (!['localhost', '127.0.0.1'].includes(hostname)) {
  Hotjar.init(2960320, 6);
  posthog.init(
    getApiHost() === 'https://apis.relume.io/'
      ? 'phc_kp2WfFciTwSdKY2nV69i7vu6Irbb49muPMoyGdBLZnX'
      : 'phc_Mh2R8iYZj8uZZsIhvxOsz8aVrGcoHwTYqrUTL9EH8Ni',
    {
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_heatmaps: false,
      capture_performance: false,
      api_host: 'https://php.relume.io',
    },
  );
}
